import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  RadioGroup,
  Radio,
  Alert,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SecurityIcon from "@mui/icons-material/Security";
import { useNotify } from "react-admin";
import { rolesApi, permissionsApi, agentsApi } from "../providers/env";
import { CreateRoleRequest } from "../pages/RolesAndPermissions/types";
import { PermissionOut, AgentOut } from "../generated";

interface CreateRoleModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  organizationId: string;
}

export const CreateRoleModal: React.FC<CreateRoleModalProps> = ({
  open,
  onClose,
  onSuccess,
  organizationId,
}) => {
  const notify = useNotify();
  const [saving, setSaving] = useState(false);
  const [allPermissions, setAllPermissions] = useState<PermissionOut[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);
  const [roleLevel, setRoleLevel] = useState<string>("user");
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [form, setForm] = useState({
    name: "",
    description: "",
  });
  // Agent access state
  const [agents, setAgents] = useState<AgentOut[]>([]);
  const [hasAllAgentAccess, setHasAllAgentAccess] = useState(true);
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([]);
  // Check if super_admin already exists for org
  const [superAdminExists, setSuperAdminExists] = useState(false);

  useEffect(() => {
    if (open) {
      fetchPermissions();
      fetchAgents();
      checkUserRole();
      checkSuperAdminExists();
      setForm({ name: "", description: "" });
      setSelectedPermissions([]);
      setRoleLevel("user");
      setHasAllAgentAccess(true);
      setSelectedAgentIds([]);
      setSuperAdminExists(false);
    }
  }, [open]);

  const checkSuperAdminExists = async () => {
    try {
      const response = await rolesApi.getRoles({ organizationId });
      const roles = response.data || [];
      const hasSuperAdmin = roles.some((r: any) => r.user_level === "super_admin");
      setSuperAdminExists(hasSuperAdmin);
    } catch (error) {
      console.error("Error checking super admin", error);
    }
  };

  const checkUserRole = () => {
    try {
      const permissions = localStorage.getItem("permissions");
      if (permissions) {
        const userInfo = JSON.parse(permissions);
        setIsSuperAdmin(userInfo.is_superuser || false);
      }
    } catch (error) {
      console.error("Error checking user role", error);
    }
  };

  // Auto-add dashboard view and agent view permissions when permissions are loaded
  useEffect(() => {
    if (allPermissions.length > 0) {
      const dashboardViewPerm = allPermissions.find(
        (p) => p.resource === 'dashboard' && p.action === 'view'
      );
      const agentViewPerm = allPermissions.find(
        (p) => p.resource === 'agent' && p.action === 'view'
      );

      const requiredPermIds = [dashboardViewPerm?.id, agentViewPerm?.id].filter(Boolean) as number[];

      if (requiredPermIds.length > 0) {
        setSelectedPermissions((prev) => {
          const newPerms = [...prev];
          requiredPermIds.forEach(id => {
            if (!newPerms.includes(id)) {
              newPerms.push(id);
            }
          });
          return newPerms;
        });
      }
    }
  }, [allPermissions]);

  const fetchPermissions = async () => {
    try {
      const response = await permissionsApi.getAllPermissions();
      setAllPermissions(response.data || []);
    } catch (error: any) {
      notify("Error loading permissions", { type: "error" });
    }
  };

  const fetchAgents = async () => {
    try {
      // Fetch agents for the target organization
      const response = await agentsApi.getAgentsByOrganization({ organizationId });
      setAgents(response.data || []);
    } catch (error: any) {
      console.error("Error loading agents", error);
      // Fallback to current user's agents
      try {
        const fallbackResponse = await agentsApi.getAgents();
        setAgents(fallbackResponse.data || []);
      } catch {
        setAgents([]);
      }
    }
  };

  const groupedPermissions = allPermissions.reduce((acc, perm) => {
    if (!acc[perm.resource]) {
      acc[perm.resource] = [];
    }
    acc[perm.resource].push(perm);
    return acc;
  }, {} as Record<string, PermissionOut[]>);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePermissionToggle = (permissionId: number) => {
    const permission = allPermissions.find((p) => p.id === permissionId);
    if (!permission) return;

    // Prevent toggling ANY dashboard permission
    if (permission.resource === 'dashboard') {
      return;
    }

    // Prevent toggling agent view permission
    if (permission.resource === 'agent' && permission.action === 'view') {
      return;
    }

    setSelectedPermissions((prev) => {
      const isRemoving = prev.includes(permissionId);

      if (isRemoving) {
        // Removing permission
        return prev.filter((id) => id !== permissionId);
      } else {
        // Adding permission
        const newPermissions = [...prev, permissionId];

        // If adding edit, delete, or create, also add view permission
        if (['edit', 'delete', 'create', 'clone'].includes(permission.action)) {
          const viewPermission = allPermissions.find(
            (p) => p.resource === permission.resource && p.action === 'view'
          );

          if (viewPermission && !newPermissions.includes(viewPermission.id)) {
            newPermissions.push(viewPermission.id);
          }
        }

        return newPermissions;
      }
    });
  };

  const handleResourceToggle = (resource: string) => {
    // Prevent toggling dashboard resource
    if (resource === 'dashboard') {
      return;
    }

    // For agent resource, exclude view permission from toggle
    let resourcePermIds = groupedPermissions[resource].map((p) => p.id);
    if (resource === 'agent') {
      const agentViewPerm = allPermissions.find(
        (p) => p.resource === 'agent' && p.action === 'view'
      );
      if (agentViewPerm) {
        resourcePermIds = resourcePermIds.filter(id => id !== agentViewPerm.id);
      }
    }

    const allSelected = resourcePermIds.every((id) =>
      selectedPermissions.includes(id)
    );

    if (allSelected) {
      setSelectedPermissions((prev) =>
        prev.filter((id) => !resourcePermIds.includes(id))
      );
    } else {
      setSelectedPermissions((prev) =>
        Array.from(new Set([...prev, ...resourcePermIds]))
    );
    }
  };

  const handleRoleLevelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const level = e.target.value;
    setRoleLevel(level);

    if (level === "super_admin") {
      setSelectedPermissions(allPermissions.map((p) => p.id));
      setHasAllAgentAccess(true);
      setSelectedAgentIds([]);
      setForm((prev) => ({
        ...prev,
        name: "Org Admin",
        description: "Full access to all organization features",
      }));
    } else if (level === "sub_admin") {
      const subAdminPerms = allPermissions
        .filter(
          (p) =>
            !(
              (p.resource === "role" && p.action === "create") ||
              (p.resource === "role" && p.action === "edit") ||
              (p.resource === "role" && p.action === "delete")
            )
        )
        .map((p) => p.id);
      setSelectedPermissions(subAdminPerms);
      setForm((prev) => ({
        ...prev,
        name: "Sub Admin",
        description: "Administrative access except role management",
      }));
    } else {
      // User level - exclude role and user management permissions
      const userPerms = allPermissions
        .filter(
          (p) =>
            p.resource !== "role" &&
            p.resource !== "user"
        )
        .map((p) => p.id);
      setSelectedPermissions(userPerms);
      setForm((prev) => ({
        ...prev,
        name: "User",
        description: "Regular user with limited permissions",
      }));
    }
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      notify("Role name is required", { type: "warning" });
      return;
    }

    // Ensure dashboard:view and agent:view are always included
    const dashboardViewPerm = allPermissions.find(
      (p) => p.resource === 'dashboard' && p.action === 'view'
    );
    const agentViewPerm = allPermissions.find(
      (p) => p.resource === 'agent' && p.action === 'view'
    );

    const requiredPermIds = [dashboardViewPerm?.id, agentViewPerm?.id].filter(Boolean) as number[];
    const finalPermissions = Array.from(new Set([...selectedPermissions, ...requiredPermIds]));

    setSaving(true);
    try {
      const payload: CreateRoleRequest = {
        organization_id: organizationId,
        name: form.name,
        description: form.description,
        permission_ids: finalPermissions,
        role_data: roleLevel,  // Send user_level as role_data
        has_all_agent_access: hasAllAgentAccess,
        agent_ids: hasAllAgentAccess ? [] : selectedAgentIds,
      };

      await rolesApi.createRole({ roleCreate: payload });
      notify("Role created successfully", { type: "success" });
      onSuccess();
      onClose();
    } catch (error: any) {
      notify(error?.response?.data?.detail || "Error creating role", {
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const formatResourceName = (resource: string) => {
    return resource
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          display: "flex", 
          alignItems: "center", 
          gap: 1,
          pb: 1
        }}
      >
        <SecurityIcon sx={{ color: "#1976d2" }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Create New Role
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ mt: 0.5, fontSize: "0.875rem" }}
          >
            Create a new role and assign permissions based on the role level
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          disabled={saving}
          size="small"
          sx={{
            "&:hover": {
              backgroundColor: "#f5f5f5"
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        <TextField
          margin="normal"
          label="Role Name"
          name="name"
          placeholder="Enter role name"
          fullWidth
          required
          value={form.name}
          onChange={handleChange}
          autoFocus
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 1,
            }
          }}
        />

        <TextField
          margin="normal"
          label="Description"
          name="description"
          placeholder="Enter role description"
          fullWidth
          multiline
          rows={2}
          value={form.description}
          onChange={handleChange}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 1,
            }
          }}
        />

        <Box sx={{ mt: 3, mb: 2 }}>
          <Typography 
            variant="subtitle2" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              fontSize: "0.875rem",
              mb: 2
            }}
          >
            Role Level
          </Typography>
          <RadioGroup
            value={roleLevel}
            onChange={handleRoleLevelChange}
            sx={{ gap: 1 }}
          >
            {/* Super Admin option - only visible to super admins and if no super_admin exists */}
            {isSuperAdmin && !superAdminExists && (
              <FormControlLabel
                value="super_admin"
                control={<Radio />}
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  p: 1.5,
                  m: 0,
                  "&:hover": {
                    backgroundColor: "#f9f9f9"
                  }
                }}
                label={
                  <Box sx={{ ml: 1 }}>
                    <Typography variant="body2" fontWeight="medium">
                      Org Admin{" "}
                      <Chip
                        label="Highest Access"
                        size="small"
                        sx={{
                          backgroundColor: "#9c27b0",
                          color: "white",
                          height: 20,
                          fontSize: "0.7rem",
                          ml: 1
                        }}
                      />
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      Full system access including organization and users
                      management
                    </Typography>
                  </Box>
                }
              />
            )}
            {isSuperAdmin && superAdminExists && (
              <Alert severity="info" sx={{ mb: 1 }}>
                Org Admin role already exists for this organization. Only one Org Admin role is allowed per organization.
              </Alert>
            )}
            <FormControlLabel
              value="user"
              control={<Radio />}
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                p: 1.5,
                m: 0,
                "&:hover": {
                  backgroundColor: "#f9f9f9"
                }
              }}
              label={
                <Box sx={{ ml: 1 }}>
                  <Typography variant="body2" fontWeight="medium">
                    User{" "}
                    <Chip 
                      label="Standard Access" 
                      size="small" 
                      sx={{
                        backgroundColor: "#9e9e9e",
                        color: "white",
                        height: 20,
                        fontSize: "0.7rem",
                        ml: 1
                      }} 
                    />
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    Regular user with limited permissions
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </Box>

        {roleLevel === "user" && (
          <Alert
            severity="info"
            sx={{
              mb: 2,
              mt: 2,
              borderRadius: 1,
              "& .MuiAlert-icon": {
                alignItems: "center"
              }
            }}
          >
            User roles have the most restricted permissions and cannot create
            other users.
          </Alert>
        )}

        {/* Agent Access Section - Hide for super_admin */}
        {roleLevel !== "super_admin" && (
        <Box sx={{ mt: 3, mb: 2 }}>
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{
              fontWeight: 600,
              fontSize: "0.875rem",
              mb: 2
            }}
          >
            Agent Access
          </Typography>
          <RadioGroup
            value={hasAllAgentAccess ? "all" : "specific"}
            onChange={(e) => {
              const isAll = e.target.value === "all";
              setHasAllAgentAccess(isAll);
              if (isAll) {
                setSelectedAgentIds([]);
              } else {
                // Remove agent create/edit/delete/clone permissions, keep only view
                const agentNonViewPerms = allPermissions
                  .filter((p) => p.resource === "agent" && p.action !== "view")
                  .map((p) => p.id);
                setSelectedPermissions((prev) =>
                  prev.filter((id) => !agentNonViewPerms.includes(id))
                );
              }
            }}
            sx={{ gap: 1 }}
          >
            <FormControlLabel
              value="all"
              control={<Radio />}
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                p: 1.5,
                m: 0,
                "&:hover": { backgroundColor: "#f9f9f9" }
              }}
              label={
                <Box sx={{ ml: 1 }}>
                  <Typography variant="body2" fontWeight="medium">
                    All Agents
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    User can access all agents in the organization
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="specific"
              control={<Radio />}
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                p: 1.5,
                m: 0,
                "&:hover": { backgroundColor: "#f9f9f9" }
              }}
              label={
                <Box sx={{ ml: 1 }}>
                  <Typography variant="body2" fontWeight="medium">
                    Specific Agents
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    User can only access selected agents
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>

          {!hasAllAgentAccess && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: "block" }}>
                Select agents ({selectedAgentIds.length} selected)
              </Typography>
              {agents.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No agents available
                </Typography>
              ) : (
                <Box sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                  gap: 1.5
                }}>
                  {agents.map((agent) => {
                    const isSelected = selectedAgentIds.includes(agent.id);
                    return (
                      <Box
                        key={agent.id}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedAgentIds((prev) => prev.filter((id) => id !== agent.id));
                          } else {
                            setSelectedAgentIds((prev) => [...prev, agent.id]);
                          }
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          p: 1.5,
                          border: isSelected ? "2px solid #1976d2" : "1px solid #e0e0e0",
                          borderRadius: 1,
                          cursor: "pointer",
                          backgroundColor: isSelected ? "#e3f2fd" : "transparent",
                          transition: "all 0.2s",
                          "&:hover": {
                            backgroundColor: isSelected ? "#bbdefb" : "#f5f5f5",
                            borderColor: "#1976d2",
                          },
                        }}
                      >
                        <Checkbox
                          checked={isSelected}
                          size="small"
                          sx={{ p: 0 }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: isSelected ? 500 : 400,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {agent.name}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}
        </Box>
        )}

        {roleLevel === "super_admin" && (
          <Alert
            severity="success"
            sx={{
              mb: 2,
              mt: 2,
              borderRadius: 1,
              "& .MuiAlert-icon": {
                alignItems: "center"
              }
            }}
          >
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              Org Admin Role
            </Typography>
            <Typography variant="caption" color="text.secondary">
              This role automatically has all permissions enabled by default. No manual permission selection needed.
            </Typography>
          </Alert>
        )}

        {roleLevel !== "super_admin" && (
          <Box sx={{ mt: 3 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{
                fontWeight: 600,
                fontSize: "0.875rem",
                mb: 2
              }}
            >
              Permissions ({selectedPermissions.length} selected)
            </Typography>

            {Object.entries(groupedPermissions).map(([resource, permissions]) => {
            // Hide 'role' and 'user' resources for user-level roles
            if (roleLevel === "user" && (resource === "role" || resource === "user")) {
              return null;
            }

            const resourcePermIds = permissions.map((p) => p.id);
            const allChecked = resourcePermIds.every((id) =>
              selectedPermissions.includes(id)
            );
            const someChecked =
              resourcePermIds.some((id) => selectedPermissions.includes(id)) &&
              !allChecked;

            return (
              <Accordion key={resource}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={allChecked}
                        indeterminate={someChecked}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleResourceToggle(resource);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {formatResourceName(resource)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {permissions.length} permissions
                        </Typography>
                      </Box>
                    }
                    onClick={(e) => e.stopPropagation()}
                  />
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ pl: 4 }}>
                    {permissions.map((perm) => {
                      const isDashboardPerm = perm.resource === 'dashboard';
                      const isAgentView = perm.resource === 'agent' && perm.action === 'view';
                      const isAgentNonView = perm.resource === 'agent' && perm.action !== 'view';
                      const isLockedBySpecificAgents = isAgentNonView && !hasAllAgentAccess;
                      const isLocked = isDashboardPerm || isAgentView || isLockedBySpecificAgents;

                      return (
                        <FormControlLabel
                          key={perm.id}
                          control={
                            <Checkbox
                              checked={selectedPermissions.includes(perm.id)}
                              onChange={() => handlePermissionToggle(perm.id)}
                              disabled={isLocked}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2">
                                {perm.action.charAt(0).toUpperCase() +
                                  perm.action.slice(1)}{" "}
                                {formatResourceName(resource)}
                                {(isDashboardPerm || isAgentView) && (
                                  <Chip
                                    label="Required"
                                    size="small"
                                    sx={{
                                      ml: 1,
                                      height: 18,
                                      fontSize: "0.65rem",
                                      backgroundColor: "#4caf50",
                                      color: "white",
                                    }}
                                  />
                                )}
                              </Typography>
                              {perm.description && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {perm.description}
                                </Typography>
                              )}
                              {isDashboardPerm && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: "block", fontStyle: "italic" }}
                                >
                                  Dashboard permissions cannot be changed
                                </Typography>
                              )}
                              {isAgentView && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: "block", fontStyle: "italic" }}
                                >
                                  Required for all roles
                                </Typography>
                              )}
                              {isLockedBySpecificAgents && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: "block", fontStyle: "italic" }}
                                >
                                  Not available with specific agent access
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      );
                    })}
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button 
          onClick={onClose} 
          disabled={saving}
          sx={{
            textTransform: "none",
            px: 2
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={saving}
          variant="contained"
          sx={{
            backgroundColor: "#1a1a1a",
            color: "white",
            textTransform: "none",
            px: 2,
            "&:hover": {
              backgroundColor: "#333333",
            },
            "&:disabled": {
              backgroundColor: "#9e9e9e",
            }
          }}
        >
          Create Role
        </Button>
      </DialogActions>
    </Dialog>
  );
};